import React, { useRef, useState, useMemo, useEffect } from 'react';
import { motion, useScroll, useTransform, useMotionValueEvent } from 'framer-motion';
import PhoneMockup from '../PhoneMockup';
import useIsMobile from '../../hooks/useIsMobile';
import HeroWalletPhoneScreen from './HeroWalletPhoneScreen';
import ServicesAppScreen from './ServicesAppScreen';
import { useLandingLanguage } from '../../context/LandingLanguageContext';

/** Kosh-style motion easing */
const KOSH_EASE = [0.16, 1, 0.3, 1];
/** Sticky offset under fixed landing nav (Kosh uses ~88px). */
const STICKY_TOP_PX = 88;
/** Extra runway after the last slide (Kosh uses 200vh; we use 0 so waitlist follows immediately). */
const TAIL_VH = 0;
/**
 * Scroll-scrubbed phone opacity.
 * u = scrollYProgress * (n + 1); section height is n × 100dvh (+ optional TAIL_VH).
 */
function opacityForScreen(j, u, n, plateauFrac = 0.72) {
  if (j < 0 || j >= n) return 0;
  if (n <= 1) return j === 0 ? 1 : 0;
  const m = n + 1;
  if (u <= 0) return j === 0 ? 1 : 0;
  if (u >= m) return j === n - 1 ? 1 : 0;
  const pf = plateauFrac;
  if (j < n - 1) {
    if (u < j) return 0;
    if (u >= j + 1) return 0;
    if (u < j + pf) return 1;
    return Math.max(0, 1 - (u - j - pf) / (1 - pf));
  }
  const prev = n - 2;
  if (u < prev + pf) return 0;
  if (u < n - 1) return Math.min(1, Math.max(0, (u - prev - pf) / (1 - pf)));
  return 1;
}

function ExperienceIcon({ name, size = 22, color = 'rgba(248,250,252,0.88)' }) {
  const s = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: 1.65,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  switch (name) {
    case 'pulse':
      return (
        <svg {...s} aria-hidden>
          <path d="M4 13h3l2-8 3 16 2-8h6" />
        </svg>
      );
    case 'layers':
      return (
        <svg {...s} aria-hidden>
          <path d="M12 3 3 8l9 5 9-5-9-5Z" />
          <path d="m3 12 9 5 9-5M3 17l9 5 9-5" />
        </svg>
      );
    case 'list':
      return (
        <svg {...s} aria-hidden>
          <path d="M9 6h11M9 12h11M9 18h11M5 6h.01M5 12h.01M5 18h.01" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...s} aria-hidden>
          <path d="M12 3 5 6v6c0 3.5 2.8 6.7 7 8 4.2-1.3 7-4.5 7-8V6l-7-3Z" />
        </svg>
      );
    case 'store':
      return (
        <svg {...s} aria-hidden>
          <path d="M3 9h18v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9Z" />
          <path d="M3 9V7l3-4h12l3 4v2" />
          <path d="M9 13v4M15 13v4" />
        </svg>
      );
    case 'offline':
      return (
        <svg {...s} aria-hidden>
          <path d="M12 20h.01" />
          <path d="M8.5 8.5 5 5M2 2l20 20M12 18a6 6 0 0 0 4.24-1.76l-4.24-4.24" />
          <path d="M16.72 11.06A6 6 0 0 0 8.06 5.94L3 10" />
        </svg>
      );
    case 'map':
      return (
        <svg {...s} aria-hidden>
          <path d="M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 .553-.894L9 2l6 2 5-2v15l-6 3-5-2z" />
          <path d="M9 2v18" />
        </svg>
      );
    case 'qrcode':
      return (
        <svg {...s} aria-hidden>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <path d="M14 14h2M18 14h2M14 18h2M18 18h2" />
        </svg>
      );
    case 'balanceTrend':
      return (
        <svg {...s} aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v8M9 13l3 3 3-3" />
        </svg>
      );
    case 'bankColumns':
      return (
        <svg {...s} aria-hidden>
          <path d="M3 21h18" />
          <path d="M5 21V10l7-4 7 4v11" />
          <path d="M9 21v-4h6v4" />
          <path d="M9 14h1.5M13 14h1.5M9 17h1.5M13 17h1.5" />
        </svg>
      );
    case 'clipboardList':
      return (
        <svg {...s} aria-hidden>
          <path d="M9 5h6l1 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7l1-2Z" />
          <path d="M9 3h6" />
          <path d="M9 10h6M9 14h6M9 18h4" />
        </svg>
      );
    case 'lockClosed':
      return (
        <svg {...s} aria-hidden>
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          <path d="M12 15v2" />
        </svg>
      );
    case 'offlineQr':
      return (
        <svg {...s} aria-hidden>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <path d="M14 14h2M18 14h2M14 18h2M18 18h2" />
          <path d="M2 22L22 2" strokeWidth="2" />
        </svg>
      );
    case 'landmark':
      return (
        <svg {...s} aria-hidden>
          <path d="M4 21h16" />
          <path d="M8 21V12l4-7 4 7v9" />
          <path d="M12 5V3" />
          <path d="M10 16h4" />
        </svg>
      );
    case 'swapArrows':
      return (
        <svg {...s} aria-hidden>
          <path d="M17 7h4v4" />
          <path d="M21 7a8 8 0 0 0-14 4" />
          <path d="M7 17H3v-4" />
          <path d="M3 17a8 8 0 0 0 14-4" />
        </svg>
      );
    default:
      return (
        <svg {...s} aria-hidden>
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
  }
}

function StripHeadline({ line1, line2, accent, size = 'desktop' }) {
  const isSm = size === 'mobile';
  const fs = isSm ? 'clamp(22px, 6vw, 32px)' : 'clamp(34px, 3.8vw, 56px)';
  return (
    <div style={{ display: 'flex', gap: isSm ? 12 : 14, alignItems: 'stretch' }}>
      <div
        aria-hidden
        style={{
          width: 3,
          flexShrink: 0,
          borderRadius: 2,
          background: accent,
          alignSelf: 'stretch',
        }}
      />
      <h2
        style={{
          margin: 0,
          fontWeight: 900,
          lineHeight: 1.06,
          letterSpacing: '-0.045em',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <span style={{ display: 'block', color: '#fafafa', fontSize: fs }}>{line1}</span>
        <span style={{ display: 'block', marginTop: '0.14em', color: accent, fontSize: fs }}>{line2}</span>
      </h2>
    </div>
  );
}

/* Phone screen mocks */

/** Step 1 — Send Money */
const SendAppScreen = () => {
  const { t } = useLandingLanguage();
  return (
  <div style={{ background: '#0F0F0F', height: '100%', fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column', padding: '14px' }}>
    {/* Header */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
      <div style={{ width: '28px', height: '28px', borderRadius: '9px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8B93A7" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
      </div>
      <p style={{ color: '#F5F1EA', fontSize: '15px', fontWeight: '700', margin: 0 }}>{t('phone.sendMoney')}</p>
    </div>

    {/* Search */}
    <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '10px 12px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8B93A7" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
      <span style={{ color: '#8B93A7', fontSize: '11px' }}>{t('phone.searchPlaceholder')}</span>
    </div>

    {/* Recent contacts label */}
    <p style={{ color: '#8B93A7', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>{t('phone.recent')}</p>

    {/* Contacts */}
    {[
      { init: 'Y', name: 'Youssef El Amrani', id: 'youssef.amrani@simopay', color: '#4F46E5', active: true },
      { init: 'F', name: 'Fatima Zahra', id: 'fatima.zahra@simopay', color: '#059669' },
      { init: 'H', name: 'Hassan Benali', id: 'hassan.benali@simopay', color: '#DC2626' },
    ].map((c, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 10px', borderRadius: '12px', marginBottom: '5px', background: c.active ? 'rgba(212,175,55,0.07)' : 'rgba(255,255,255,0.03)', border: c.active ? '1px solid rgba(212,175,55,0.2)' : '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>{c.init}</div>
        <div style={{ flex: 1 }}>
          <p style={{ color: '#F5F1EA', fontSize: '12px', fontWeight: '600', margin: 0 }}>{c.name}</p>
          <p style={{ color: '#8B93A7', fontSize: '9px', margin: 0 }}>{c.id}</p>
        </div>
        {c.active && <span style={{ color: '#D4AF37', fontSize: '9px', fontWeight: '700' }}>{t('phone.select')}</span>}
      </div>
    ))}

    {/* Amount entry */}
    <div style={{ marginTop: 'auto' }}>
      <div style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '14px', padding: '12px 14px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#8B93A7', fontSize: '10px' }}>{t('phone.amount')}</span>
        <span style={{ color: '#D4AF37', fontSize: '20px', fontWeight: '900', letterSpacing: '-0.5px' }}>300 MAD</span>
      </div>
      <div style={{ background: 'linear-gradient(135deg, #D4AF37, #B8962E)', borderRadius: '12px', padding: '12px', textAlign: 'center', boxShadow: '0 6px 20px rgba(212,175,55,0.3)' }}>
        <span style={{ color: '#0F0F0F', fontSize: '12px', fontWeight: '800' }}>{t('phone.sendNow')}</span>
      </div>
      <p style={{ color: '#8B93A7', fontSize: '9px', textAlign: 'center', margin: '6px 0 0' }}>{t('phone.zeroFees')}</p>
    </div>
  </div>
  );
};

/** Step 2 — Top Up */
const TopUpAppScreen = () => {
  const { t } = useLandingLanguage();
  return (
  <div style={{ background: '#0F0F0F', height: '100%', fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column', padding: '14px' }}>
    {/* Header */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
      <div style={{ width: '28px', height: '28px', borderRadius: '9px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8B93A7" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
      </div>
      <p style={{ color: '#F5F1EA', fontSize: '15px', fontWeight: '700', margin: 0 }}>{t('phone.topUpWallet')}</p>
    </div>

    {/* Current balance */}
    <div style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0.04) 100%)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '16px', padding: '14px 16px', marginBottom: '16px', textAlign: 'center' }}>
      <p style={{ color: '#8B93A7', fontSize: '9px', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('phone.currentBalance')}</p>
      <p style={{ color: '#D4AF37', fontSize: '28px', fontWeight: '800', margin: 0, letterSpacing: '-1.2px', lineHeight: 1 }}>18,224<span style={{ fontSize: '14px', color: '#8B93A7', fontWeight: '400', marginLeft: '3px' }}>.00 MAD</span></p>
    </div>

    {/* Amount selector */}
    <p style={{ color: '#8B93A7', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>{t('phone.selectAmount')}</p>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px', marginBottom: '12px' }}>
      {['+100', '+200', '+500', '+1,000'].map((amt, i) => (
        <div key={i} style={{ background: i === 2 ? 'rgba(212,175,55,0.1)' : 'rgba(255,255,255,0.04)', border: i === 2 ? '1px solid rgba(212,175,55,0.3)' : '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '10px', textAlign: 'center' }}>
          <span style={{ color: i === 2 ? '#D4AF37' : '#F5F1EA', fontSize: '14px', fontWeight: '700' }}>{amt}</span>
          <span style={{ color: '#8B93A7', fontSize: '10px', marginLeft: '3px' }}>MAD</span>
        </div>
      ))}
    </div>

    {/* Banks */}
    <p style={{ color: '#8B93A7', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>{t('phone.payVia')}</p>
    {[
      { name: 'CIH Bank', badge: t('phone.instant'), active: true },
      { name: 'Barid Al Maghrib', badge: t('phone.instant') },
      { name: 'Attijariwafa Bank', badge: t('phone.underOneMin') },
    ].map((bank, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: '11px', marginBottom: '5px', background: bank.active ? 'rgba(212,175,55,0.06)' : 'rgba(255,255,255,0.03)', border: bank.active ? '1px solid rgba(212,175,55,0.2)' : '1px solid rgba(255,255,255,0.05)' }}>
        <span style={{ color: bank.active ? '#F5F1EA' : '#8B93A7', fontSize: '11px', fontWeight: '600' }}>{bank.name}</span>
        <span style={{ color: '#D4AF37', fontSize: '9px', fontWeight: '700', background: 'rgba(212,175,55,0.12)', padding: '2px 7px', borderRadius: '100px' }}>{bank.badge}</span>
      </div>
    ))}

    <div style={{ marginTop: '10px', background: 'linear-gradient(135deg, #D4AF37, #B8962E)', borderRadius: '12px', padding: '12px', textAlign: 'center', boxShadow: '0 6px 20px rgba(212,175,55,0.3)' }}>
      <span style={{ color: '#0F0F0F', fontSize: '12px', fontWeight: '800' }}>{t('phone.topUpCta')}</span>
    </div>
  </div>
  );
};

/** Step 3 — Scan & Pay */
const ScanAppScreen = () => {
  const { t } = useLandingLanguage();
  return (
  <div style={{ background: '#0F0F0F', height: '100%', fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column', padding: '14px', alignItems: 'center' }}>
    {/* Header */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', alignSelf: 'flex-start' }}>
      <div style={{ width: '28px', height: '28px', borderRadius: '9px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8B93A7" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
      </div>
      <p style={{ color: '#F5F1EA', fontSize: '15px', fontWeight: '700', margin: 0 }}>{t('phone.scanPay')}</p>
    </div>

    {/* Scanner viewfinder */}
    <div style={{ width: '160px', height: '160px', position: 'relative', marginBottom: '14px' }}>
      {/* Camera background */}
      <div style={{ width: '100%', height: '100%', borderRadius: '16px', background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.08)' }}>
        {/* Corner markers */}
        {[['0px','0px','2px 0 0 2px','2px 0 0 0'], ['0px','auto','2px 0 2px 0','0 2px 0 0'], ['auto','0px','0 0 2px 2px','2px 0 0 0'], ['auto','auto','0 0 0 0','0 2px 0 0']].map(([t, r, _, __], i) => (
          <div key={i} style={{ position: 'absolute', top: t !== 'auto' ? t : undefined, right: r !== 'auto' ? r : undefined, bottom: t === 'auto' ? '0px' : undefined, left: r === 'auto' ? '0px' : undefined, width: '24px', height: '24px', borderTop: t !== 'auto' ? '2.5px solid #D4AF37' : 'none', borderLeft: r === 'auto' ? '2.5px solid #D4AF37' : 'none', borderRight: r !== 'auto' ? '2.5px solid #D4AF37' : 'none', borderBottom: t === 'auto' ? '2.5px solid #D4AF37' : 'none', borderRadius: i === 0 ? '4px 0 0 0' : i === 1 ? '0 4px 0 0' : i === 2 ? '0 0 0 4px' : '0 0 4px 0' }} />
        ))}
        {/* Scan line animation */}
        <div style={{ position: 'absolute', width: '80%', height: '2px', background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)', animation: 'scan-line 2.2s ease-in-out infinite', top: '22%' }} />
        {/* QR icon */}
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth="1.5">
          <rect x="3" y="3" width="7" height="7" rx="1"/>
          <rect x="14" y="3" width="7" height="7" rx="1"/>
          <rect x="3" y="14" width="7" height="7" rx="1"/>
          <path d="M14 14h2M18 14h2M14 18h2M18 18h2"/>
        </svg>
      </div>
    </div>

    <p style={{ color: '#8B93A7', fontSize: '11px', margin: '0 0 14px', textAlign: 'center' }}>{t('phone.pointCamera')}</p>

    {/* OR divider */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', marginBottom: '14px' }}>
      <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
      <span style={{ color: '#8B93A7', fontSize: '9px', fontWeight: '600' }}>{t('phone.or')}</span>
      <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
    </div>

    {/* My QR */}
    <p style={{ color: '#8B93A7', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px', alignSelf: 'flex-start' }}>{t('phone.myQr')}</p>
    <div style={{ background: '#fff', borderRadius: '12px', padding: '10px', marginBottom: '10px' }}>
      <svg width="80" height="80" viewBox="0 0 96 96">
        <rect width="96" height="96" fill="white"/>
        <rect x="4" y="4" width="30" height="30" fill="none" stroke="#0F0F0F" strokeWidth="4"/>
        <rect x="10" y="10" width="18" height="18" fill="#0F0F0F"/>
        <rect x="62" y="4" width="30" height="30" fill="none" stroke="#0F0F0F" strokeWidth="4"/>
        <rect x="68" y="10" width="18" height="18" fill="#0F0F0F"/>
        <rect x="4" y="62" width="30" height="30" fill="none" stroke="#0F0F0F" strokeWidth="4"/>
        <rect x="10" y="68" width="18" height="18" fill="#0F0F0F"/>
        {[[44,4],[50,4],[56,4],[44,10],[56,10],[44,16],[50,16],[56,16],[44,22],[44,28],[50,28],[56,28],[4,44],[16,44],[22,44],[28,44],[4,50],[22,50],[4,56],[10,56],[28,56],[44,44],[56,44],[62,44],[68,44],[44,50],[62,50],[44,56],[50,56],[44,62],[56,62],[44,68],[50,68],[56,68],[62,68],[44,74],[56,74],[62,74],[44,80],[50,80],[68,80],[80,80],[44,86],[50,86],[56,86],[62,86],[68,86],[80,86]].map(([x, y], i) => (
          <rect key={i} x={x} y={y} width="4" height="4" fill="#0F0F0F"/>
        ))}
        <rect x="44" y="44" width="8" height="8" fill="#D4AF37"/>
      </svg>
    </div>
    <p style={{ color: '#8B93A7', fontSize: '9px', textAlign: 'center', margin: 0 }}>ahmed@simopay</p>
    <style>{`@keyframes scan-line { 0% { top: 18%; opacity: 0.6; } 50% { top: 72%; opacity: 1; } 100% { top: 18%; opacity: 0.6; } }`}</style>
  </div>
  );
};

/* Progress dots — scroll step indicator */
const ProgressDots = ({ total, active, horizontal = false, accent }) => (
  <div style={{ display: 'flex', flexDirection: horizontal ? 'row' : 'column', gap: '10px', alignItems: 'center' }}>
    {Array.from({ length: total }).map((_, i) => (
      <motion.div
        key={i}
        animate={horizontal
          ? { width: i === active ? '28px' : '8px', opacity: i === active ? 1 : 0.32 }
          : { height: i === active ? '28px' : '8px', opacity: i === active ? 1 : 0.32 }
        }
        transition={{ duration: 0.38, ease: KOSH_EASE }}
        style={horizontal
          ? { height: '3px', borderRadius: '100px', background: accent }
          : { width: '3px', borderRadius: '100px', background: accent }
        }
      />
    ))}
  </div>
);

function ScrollTailSpacer() {
  if (TAIL_VH <= 0) return null;
  return (
    <div
      aria-hidden
      style={{
        height: `${TAIL_VH * 100}vh`,
        minHeight: `${TAIL_VH * 100}dvh`,
        flexShrink: 0,
      }}
    />
  );
}

function LeftCopyArticle({ step }) {
  return (
    <article
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        paddingRight: '8px',
      }}
    >
      <div style={{ minWidth: 0, maxWidth: '420px', width: '100%' }}>
        <StripHeadline line1={step.line1} line2={step.line2} accent={step.accent} size="desktop" />
        <p
          style={{
            color: 'rgba(156,163,175,0.95)',
            fontSize: 'clamp(14px, 1.15vw, 17px)',
            lineHeight: 1.65,
            marginTop: '20px',
            marginBottom: 0,
          }}
        >
          {step.sub}
        </p>
      </div>
    </article>
  );
}

function FeatureIconTile({ accent, children }) {
  return (
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        background: `${accent}12`,
        border: `1px solid ${accent}38`,
      }}
    >
      {children}
    </div>
  );
}

function RightFeaturesArticle({ step }) {
  const iconColor = `${step.accent}e6`;
  return (
    <article
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        paddingLeft: '8px',
      }}
    >
      <div style={{ width: '100%', minWidth: 0 }}>
        {step.features.map((feat, i) => (
          <div key={`${step.line1}-${feat.label}`}>
            {i > 0 && <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)' }} />}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 0' }}>
              <FeatureIconTile accent={step.accent}>
                <ExperienceIcon name={feat.iconKey} size={20} color={iconColor} />
              </FeatureIconTile>
              <span style={{ color: 'rgba(156,163,184,0.98)', fontSize: 'clamp(13px, 1.05vw, 16px)', fontWeight: 500 }}>
                {feat.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

/** Mobile: two stacked panels per step, no scroll-scrubbed crossfade. */
function MobileStaticStep({ step }) {
  const panel = {
    width: '100%',
    maxWidth: 420,
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
    borderRadius: 20,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
  };
  return (
    <article
      style={{
        width: '100%',
        maxWidth: 420,
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: 'clamp(32px, 9vw, 48px)',
        padding: 0,
      }}
    >
      <div
        style={{
          ...panel,
          padding: 'clamp(14px, 4vw, 22px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'clamp(10px, 2.5dvh, 18px)',
          marginBottom: 'clamp(14px, 3dvh, 20px)',
        }}
      >
        <div style={{ width: '100%', textAlign: 'left' }}>
          <StripHeadline line1={step.line1} line2={step.line2} accent={step.accent} size="mobile" />
          <p
            style={{
              color: 'rgba(156,163,175,0.95)',
              fontSize: 'clamp(12px, 3.4vw, 15px)',
              lineHeight: 1.58,
              margin: '10px 0 0',
            }}
          >
            {step.sub}
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <PhoneMockup size="sm" glow minimalChrome>
            <div style={{ height: '100%' }}>{step.screen}</div>
          </PhoneMockup>
        </div>
      </div>
      <div
        style={{
          ...panel,
          padding: 'clamp(12px, 3.5vw, 18px)',
          background: 'rgba(255,255,255,0.025)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {step.features.map((feat, j) => (
          <div key={`${step.line1}-static-${feat.label}`}>
            {j > 0 && <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '0 -4px' }} />}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 2px' }}>
              <FeatureIconTile accent={step.accent}>
                <ExperienceIcon name={feat.iconKey} size={18} color={`${step.accent}e6`} />
              </FeatureIconTile>
              <span
                style={{
                  color: 'rgba(156,163,184,0.98)',
                  fontSize: '11px',
                  fontWeight: 500,
                  lineHeight: 1.35,
                  textAlign: 'left',
                }}
              >
                {feat.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

/**
 * KOSH `#fourth-section` pattern: left/right columns are stacked `100vh` blocks + `200vh` tail;
 * center column is sticky under the nav with phone layers crossfaded from scroll progress.
 */
const AppExperience = () => {
  const sectionRef = useRef(null);
  const [accentIdx, setAccentIdx] = useState(0);
  const [dotStep, setDotStep] = useState(0);
  const isMobile = useIsMobile();
  const { t } = useLandingLanguage();

  const steps = useMemo(
    () => [
      {
        screen: <ScanAppScreen />,
        line1: t('appExperience.step0.headline1'),
        line2: t('appExperience.step0.headline2'),
        sub: t('appExperience.step0.sub'),
        features: [
          { iconKey: 'store',      label: t('appExperience.step0.f1') },
          { iconKey: 'offlineQr',  label: t('appExperience.step0.f2') },
          { iconKey: 'landmark',   label: t('appExperience.step0.f3') },
          { iconKey: 'swapArrows', label: t('appExperience.step0.f4') },
        ],
        accent: '#D4AF37',
      },
      {
        screen: <ServicesAppScreen />,
        line1: t('appExperience.step1.headline1'),
        line2: t('appExperience.step1.headline2'),
        sub: t('appExperience.step1.sub'),
        features: [
          { iconKey: 'map',    label: t('appExperience.step1.f1') },
          { iconKey: 'store',  label: t('appExperience.step1.f2') },
          { iconKey: 'layers', label: t('appExperience.step1.f3') },
          { iconKey: 'pulse',  label: t('appExperience.step1.f4') },
        ],
        accent: '#FBBF24',
      },
      {
        screen: <HeroWalletPhoneScreen />,
        line1: t('appExperience.step2.headline1'),
        line2: t('appExperience.step2.headline2'),
        sub: t('appExperience.step2.sub'),
        features: [
          { iconKey: 'balanceTrend',  label: t('appExperience.step2.f1') },
          { iconKey: 'bankColumns',   label: t('appExperience.step2.f2') },
          { iconKey: 'clipboardList', label: t('appExperience.step2.f3') },
          { iconKey: 'lockClosed',    label: t('appExperience.step2.f4') },
        ],
        accent: '#FBBF24',
      },
    ],
    [t],
  );

  const n = steps.length;
  /** Same scrub curve as desktop so phone + copy handoff timing matches. */
  const scrollBlendPlateau = 0.72;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  const blendU = useTransform(scrollYProgress, (p) => p * (n + 1));

  const op0 = useTransform(scrollYProgress, (p) => opacityForScreen(0, p * (n + 1), n, scrollBlendPlateau));
  const op1 = useTransform(scrollYProgress, (p) => opacityForScreen(1, p * (n + 1), n, scrollBlendPlateau));
  const op2 = useTransform(scrollYProgress, (p) => opacityForScreen(2, p * (n + 1), n, scrollBlendPlateau));
  const layerOpacities = [op0, op1, op2];

  const isMobileRef = useRef(isMobile);
  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useMotionValueEvent(blendU, 'change', (u) => {
    if (isMobileRef.current) return;
    setAccentIdx(Math.min(n - 1, Math.max(0, Math.floor(u))));
    setDotStep(Math.min(n - 1, Math.max(0, Math.round(u - 0.35))));
  });

  const accentStep = steps[accentIdx];

  return (
    <section
      ref={sectionRef}
      style={{
        background: '#0F0F0F',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      {isMobile ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            background:
              'radial-gradient(ellipse 58% 55% at 50% 42%, rgba(212,175,55,0.1) 0%, transparent 55%), radial-gradient(ellipse 50% 45% at 50% 58%, rgba(167,139,250,0.08) 0%, transparent 60%)',
          }}
        />
      ) : (
        <motion.div
          animate={{ background: `radial-gradient(ellipse 58% 55% at 50% 48%, ${accentStep.accent}16 0%, transparent 70%)` }}
          transition={{ duration: 0.65 }}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}
        />
      )}

      {!isMobile ? (
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            maxWidth: '1240px',
            margin: '0 auto',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'minmax(260px, 1fr) auto minmax(260px, 1fr)',
            gap: '0 clamp(32px, 5vw, 72px)',
            padding: '0 clamp(28px, 5vw, 96px)',
            alignItems: 'stretch',
          }}
        >
          <div style={{ minWidth: 0 }}>
            {steps.map((s, i) => (
              <LeftCopyArticle key={`lc-${i}`} step={s} />
            ))}
            <ScrollTailSpacer />
          </div>

          <div style={{ minWidth: 0, position: 'relative' }}>
            <div
              style={{
                position: 'sticky',
                top: STICKY_TOP_PX,
                height: `calc(100dvh - ${STICKY_TOP_PX}px)`,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: 'clamp(8px, 2.5dvh, 36px)',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '42%',
                    transform: 'translate(-50%, -50%)',
                    width: 'min(420px, 140%)',
                    height: 'min(420px, 130%)',
                    background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%)',
                    filter: 'blur(56px)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />
                <PhoneMockup size="lg" glow minimalChrome>
                  <div style={{ position: 'relative', width: '100%', height: '100%', zIndex: 1 }}>
                    {steps.map((s, i) => (
                      <motion.div
                        key={`layer-${i}`}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          opacity: layerOpacities[i],
                          pointerEvents: 'none',
                        }}
                      >
                        <div style={{ height: '100%', pointerEvents: 'auto' }}>{s.screen}</div>
                      </motion.div>
                    ))}
                  </div>
                </PhoneMockup>
              </div>
            </div>
          </div>

          <div style={{ minWidth: 0 }}>
            {steps.map((s, i) => (
              <RightFeaturesArticle key={`rc-${i}`} step={s} />
            ))}
            <ScrollTailSpacer />
          </div>
        </div>
      ) : (
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            paddingLeft: 'clamp(16px, 5vw, 28px)',
            paddingRight: 'clamp(16px, 5vw, 28px)',
            paddingTop: 'clamp(12px, 3dvh, 24px)',
            paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
            boxSizing: 'border-box',
          }}
        >
          {steps.map((s, i) => (
            <MobileStaticStep key={`m-static-${i}`} step={s} />
          ))}
        </div>
      )}
    </section>
  );
};

export default AppExperience;
